import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { Dialog, useDialog } from "@sembark-travel/ui/dialog"
import {
	Alert,
	Box,
	Button,
	Col,
	Component,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	joinAttributes,
	Spinner,
	Stack,
	Text,
	useBreakpoints,
} from "@sembark-travel/ui/base"
import {
	Form,
	GetFieldValue,
	SelectField,
	SwitchInputField,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui/form"
import { SelectTermsAndConditions, XHR } from "../TermsAndConditions/List"
import { ITermsAndCondition } from "../TermsAndConditions"
import { showSnackbar } from "@sembark-travel/ui/snackbar"
import * as Validator from "yup"

type Ifrom = {
	title: string
	terms_and_conditions: ITermsAndCondition[]
	default_hsn: string
	display_hsn: boolean
	display_place_of_supply: boolean
	terms_section_settings: { title: string }
}

const validationSchema = validateFormValues(
	Validator.object().shape({
		title: Validator.string().required("Title is required"),
		terms_section_settings: Validator.object().shape({
			title: Validator.string().required("Terms section settings is required"),
		}),
	})
)

export function InvoiceTemplates() {
	const xhr = useXHR()
	const { xs } = useBreakpoints()

	const { data, error, mutate } = useSWR("invoice-templates", async () => {
		const [templateResp, featuredResp] = await Promise.all([
			xhr.get<{ data: Array<Omit<Ifrom, "terms_and_conditions">> }>(
				"invoice-templates?include=use_fallback"
			),
			XHR(xhr).get({ featured_in: "invoices", limit: "all" }),
		])

		return {
			templatesData: templateResp.data.data,
			termsAndConditions: featuredResp.data,
		}
	})

	const { hasPermission } = useCheckPermissions()
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	if (!data) {
		if (error) {
			return (
				<Alert status="error">
					{error.message || "Something went wrong. Please try after sometime."}
				</Alert>
			)
		}
		return <Spinner alignCenter padding="4" />
	}

	return (
		<Stack gap="4">
			<Inline justifyContent="between" gap="4" alignItems="center">
				<Inline gap="4" alignItems="center">
					{xs ? null : (
						<Inline
							size="12"
							rounded="full"
							bgColor="default"
							borderWidth="1"
							alignItems="center"
							justifyContent="center"
							color="muted"
							fontFamily="mono"
						>
							PDF
						</Inline>
					)}
					<Stack gap="1">
						<Heading as="h3">Proforma Invoice Template Configurations</Heading>
						<Text>
							These configuration will be applied to Proforma Invoice only.
						</Text>
					</Stack>
				</Inline>
				{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
					<Box>
						<Button
							onClick={openDialog}
							level={!data.templatesData.length ? "primary" : "secondary"}
							status="primary"
							size="sm"
						>
							<Icons.Pencil /> Edit
						</Button>
					</Box>
				) : null}
			</Inline>
			<Divider marginY="0" />

			<Box>
				{!data.templatesData.length ? (
					<Box padding="4" bgColor="default" rounded="md" borderWidth="1">
						<Text>Using default Invoice template configurations.</Text>
					</Box>
				) : (
					data.templatesData.map((template) => (
						<Stack key={template.title} gap="4">
							<Text>
								<b>Title Customization</b>: {template.title}
							</Text>
							<Text>
								<b>Show Place of Supply</b>:{" "}
								{template.display_place_of_supply ? "Yes" : "No"}
							</Text>
							<Text>
								<b>Show HSN/SAC Number</b>:{" "}
								{template.display_hsn ? "Yes" : "No"}
							</Text>
							<Text>
								<b>Default HSN/SAC Number</b>:{" "}
								{template.default_hsn ? (
									template.default_hsn
								) : (
									<Text color="muted" as="span">
										N/A
									</Text>
								)}
							</Text>
							<Text>
								<b>Title for T&C Section</b>:{" "}
								{template.terms_section_settings.title || ""}
							</Text>

							<Inline flexWrap={"wrap"}>
								<b>Terms and Conditions Section</b>:{" "}
								{data?.termsAndConditions.length
									? joinAttributes(
											...(data?.termsAndConditions.map((item) => item.name) ||
												"")
										)
									: "No Terms and Conditions selected"}
							</Inline>
						</Stack>
					))
				)}{" "}
			</Box>

			<Dialog
				open={isDialogOpen}
				onClose={closeDialog}
				title="Update Proforma Invoice Template"
				lg
			>
				<Form<Ifrom>
					onSubmit={withServerErrors(async (values) => {
						const {
							title,
							default_hsn,
							display_hsn,
							display_place_of_supply,
							terms_section_settings,
						} = values
						await xhr.post(`invoice-templates`, {
							terms_section_settings,
							title,
							default_hsn: display_hsn ? default_hsn || "" : "",
							display_hsn: display_hsn ? 1 : 0,
							display_place_of_supply: display_place_of_supply ? 1 : 0,
							terms_and_conditions: values.terms_and_conditions.map(
								(u) => u.id
							),
						})
						mutate()
						closeDialog()
						showSnackbar("Successfully Changed")
					})}
					subscription={{ submitting: true }}
					initialValues={{
						title: data.templatesData[0].title,
						default_hsn: data.templatesData[0].default_hsn || "",
						display_hsn: data.templatesData[0].display_hsn,
						display_place_of_supply:
							data.templatesData[0].display_place_of_supply,
						terms_and_conditions: data?.termsAndConditions,
						terms_section_settings: {
							title: data?.templatesData[0]?.terms_section_settings.title,
						},
					}}
					validate={validationSchema}
				>
					{({ handleSubmit, submitting }) => (
						<form onSubmit={handleSubmit}>
							<Dialog.Body>
								<Stack gap="4">
									<Grid gap="6">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading fontSize="md">Title Customization</Heading>
												<Text color="muted">
													Configure title settings like Draft Invoice, Estimate
													Invoice, Preliminary Invoice.
												</Text>
											</Stack>
										</Col>
										<Col>
											<Component initialState={false}>
												{({ state: editing, setState }) =>
													!editing ? (
														<Stack gap="1">
															<Text fontWeight="semibold">Section Title</Text>
															<Inline
																borderWidth="1"
																rounded="md"
																paddingLeft="4"
																bgColor="subtle"
																gap="4"
																justifyContent="between"
																alignItems="center"
																maxWidth={"sm"}
															>
																<Text>{data.templatesData[0].title}</Text>
																<Button
																	onClick={() => {
																		setState(true)
																	}}
																	level="tertiary"
																>
																	<Icons.Pencil />
																</Button>
															</Inline>
														</Stack>
													) : (
														<TextInputField
															name="title"
															label="Section Title"
															type="text"
															placeholder="e.g Proforma Invoice"
															maxWidth={"sm"}
														/>
													)
												}
											</Component>
										</Col>
									</Grid>
									<Divider marginY="0" />
									<Grid gap="6">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading fontSize="md">Place of Supply</Heading>
												<Text color="muted">
													Customize the settings of Place of Supply section
												</Text>
											</Stack>
										</Col>
										<Col>
											<Stack gap="4">
												<SwitchInputField
													name="display_place_of_supply"
													label="Show Place of Supply"
												/>
											</Stack>
										</Col>
									</Grid>

									<Divider marginY="0" />

									<Grid gap="6">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading fontSize="md">HSN/SAC</Heading>
												<Text color="muted">
													Customize the settings of HSN/SAC section
												</Text>
											</Stack>
										</Col>
										<Col>
											<Inline
												justifyContent="start"
												gap={"4"}
												alignItems={"start"}
											>
												<SwitchInputField
													name="display_hsn"
													label="Show HSN/SAC"
												/>
												<GetFieldValue<boolean | undefined> name="display_hsn">
													{({ value: hsn }) => {
														return hsn ? (
															<TextInputField
																label="Default HSN/SAC"
																name="default_hsn"
																type="text"
															/>
														) : null
													}}
												</GetFieldValue>
											</Inline>
										</Col>
									</Grid>
									<Divider marginY="0" />
									<Grid gap="6">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading fontSize="md">Title for TnC. Section</Heading>
												<Text color="muted">Customize the section title.</Text>
											</Stack>
										</Col>
										<Col>
											<Stack gap="4">
												<TextInputField
													name="terms_section_settings.title"
													label="Section Title"
													type="text"
													placeholder="e.g Terms and Conditions"
													maxWidth={"sm"}
												/>
											</Stack>
										</Col>
									</Grid>
									<Divider marginY="0" />

									<Grid gap="6">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading fontSize="md">
													Terms and Conditions Selection
												</Heading>
												<Text color="muted">
													The selected terms and conditions will appear on the
													proforma invoice
												</Text>
											</Stack>
										</Col>
										<Col>
											<SelectField
												name="terms_and_conditions"
												label="Terms and Conditions"
												select={SelectTermsAndConditions}
												multiple
												fetchOnMount={true}
												creatable
											/>
										</Col>
									</Grid>
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Button type="submit" disabled={submitting}>
									Save Details
								</Button>
								<Button disabled={submitting} onClick={closeDialog}>
									Cancel
								</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</Stack>
	)
}
